/**
 * A PERFECT duck near the swing's bottom raises the salmon ladder's actual bar and hand anchor
 * over one rung. The body keeps swinging around that moving anchor, and seating earns the rung.
 * The original apex pull/kip remains isolated below for exact playback of saved legacy tapes.
 */

import type { EventBuffer } from "../events";
import { abs, atan2, cos, PI, sin, sqrt } from "../fastmath";
import { emitGrade, gradeOffset } from "../grades";
import { linkOnPop } from "../link";
import type { SimState } from "../state";
import type { CompiledTuning } from "../tuning";
import { DT, Ev, Grade, GradeKind, HZ } from "../types";
import type { World } from "../world";
import { attachPhysics } from "./bar";

/** The swing turned round on a ladder: an apex with no pop on it breaks the chain. */
export function apexPassed(s: SimState): void {
  if (s.popping === 0) s.ladderChain = 0;
}

/** Cosine of the swing's amplitude from its energy (the angle it would stop at); 1 is no swing at all. */
function cosAmplitude(t: CompiledTuning, s: SimState): number {
  if (t.swingG <= 0) return cos(s.theta);
  const v = s.omega * s.L;
  const e = 0.5 * v * v - t.swingG * s.L * cos(s.theta);
  return -e / (t.swingG * s.L);
}

/** The swing's amplitude, radians, from its cosine (acos through atan2, which fastmath has). */
function amplitude(c: number): number {
  if (c >= 1) return 0;
  if (c <= -1) return PI;
  return atan2(sqrt(1 - c * c), c);
}

/**
 * Signed time from the apex, ms: negative on the way up to it, positive on the way down from it
 * (since the turn). On the way up it is the smaller of two estimates, the angle still to go over
 * half the rate (the swing slows to a stop there) and the rate over the deceleration at this angle,
 * so a swing trimmed at the pump ceiling, which turns short of its energy's amplitude, still reads
 * as arriving.
 */
function apexOffsetMs(t: CompiledTuning, s: SimState, cosAmp: number): number {
  if (s.theta * s.omega > 0) {
    const w = abs(s.omega);
    const left = amplitude(cosAmp) - abs(s.theta);
    const byAngle = w > 1e-6 ? (2 * (left > 0 ? left : 0)) / w : 1e9;
    const decel = (t.swingG / s.L) * sin(abs(s.theta));
    const byRate = decel > 1e-6 ? w / decel : 1e9;
    return -(byAngle < byRate ? byAngle : byRate) * 1000;
  }
  return ((s.tick - s.lastApexTime) * 1000) / HZ;
}

/**
 * A pull, or the tuck let go (`kip`), while on a ladder rung. The pull is the deliberate verb and
 * misses when it is off: early under load, late, or on a swing too narrow to lift the bar. The kip
 * is the pump's own release, so off the window it is just that (the tuck pump lets go early all
 * the time) and only a late kip or one on a narrow swing counts as a miss.
 */
export function tryPop(world: World, s: SimState, t: CompiledTuning, ev: EventBuffer, kip: boolean): void {
  if (t.ladderPerfectDuck > 0 || s.popping === 1) return;
  if (kip && s.tuck < t.ladderKipTuck) return;
  const ladder = world.barLadder[s.barId]!;
  const rung = world.barRung[s.barId]!;
  const cosAmp = cosAmplitude(t, s);
  const narrow = cosAmp > t.cosLadderMinAmplitude;
  const offset = apexOffsetMs(t, s, cosAmp);
  let grade: number = gradeOffset(offset, t.ladderPopPerfectMs, t.ladderPopEarlyMs, t.ladderPopLateMs);
  if (grade === Grade.Ok || narrow) grade = Grade.Miss;
  if (kip && grade === Grade.Miss && offset < 0 && !narrow) return;
  s.lastLadderGrade = grade;
  if (grade === Grade.Miss) {
    // The bar clips the rung.
    s.ladderChain = 0;
    emitGrade(s, t, ev, GradeKind.Ladder, grade, offset);
    if (t.ladderMissFalls >= 1) {
      s.reach = 0; // the grip is lost: the next tick's release rule lets go
    } else s.omega *= t.ladderMissRetention;
    return;
  }
  if (rung >= world.ladderRungs[ladder]! - 1) {
    // The top rung: nowhere to go, the pull is spent for nothing.
    emitGrade(s, t, ev, GradeKind.Ladder, Grade.Ok, offset);
    return;
  }
  // The pop: the swing's velocity plus the pull's lift, then the rung above taken where the body is.
  const tx = cos(s.theta);
  const ty = sin(s.theta);
  const vt = s.omega * s.L;
  const lift = t.ladderPopLift * (grade === Grade.Perfect ? t.ladderPopPerfectScale : 1);
  const vx = vt * tx;
  const vy = vt * ty + lift;
  s.ax = (vx - s.vx) / DT;
  s.ay = (vy - s.vy) / DT;
  s.vx = vx;
  s.vy = vy;
  const above = s.barId + 1;
  s.ladderChain++;
  attachPhysics(world, s, t, ev, above);
  s.attachLeft = t.ladderPopTicks;
  s.attachTotal = t.ladderPopTicks;
  s.popping = 1;
  s.ladderRung = rung + 1;
  ev.push(Ev.Popped, s.tick, ladder, rung + 1, grade);
  emitGrade(s, t, ev, GradeKind.Ladder, grade, offset);
  linkOnPop(s, t, ev, above);
}

/** A fresh duck earns one rung only inside the existing PERFECT pump timing window. */
export function tryDuckClimb(world: World, s: SimState, t: CompiledTuning, ev: EventBuffer): void {
  if (s.popping === 1 || s.lastPumpTick !== s.tick) return;
  const ladder = world.barLadder[s.barId]!;
  const rung = world.barRung[s.barId]!;
  if (ladder < 0 || rung >= world.ladderRungs[ladder]! - 1) return;
  s.lastLadderGrade = s.lastPumpGrade;
  if (s.lastPumpGrade !== Grade.Perfect) {
    s.ladderChain = 0;
    return;
  }
  // The press is normally just before the bottom. The same crossing remains spent immediately
  // after it, even if the player rapidly releases and presses again within the PERFECT window.
  const bottom = s.ladderCrossings + (s.theta * s.omega < 0 ? 1 : 0);
  if (bottom === s.ladderDuckBottom) return;
  if (bottom !== s.ladderDuckBottom + 1) s.ladderChain = 0;
  s.ladderDuckBottom = bottom;
  s.ladderLiftY = 0;
  s.ladderLiftTick = 0;
  s.ladderLiftVelocity = 0;
  s.popping = 1;
  emitGrade(s, t, ev, GradeKind.Ladder, Grade.Perfect, s.lastPumpOffsetMs);
}

/** Lift the actual hand/bar anchor continuously; the next rung is earned only when it seats. */
export function tickLadderClimb(world: World, s: SimState, t: CompiledTuning, ev: EventBuffer): void {
  s.ladderLiftVelocity = 0;
  if (s.popping !== 1) return;
  const ladder = world.barLadder[s.barId]!;
  if (ladder < 0) return;
  const spacing = world.ladderSpacing[ladder]!;
  const before = s.ladderLiftY;
  // Taller rungs take longer instead of adding an oversized vertical launch on a mid-climb drop.
  const speedTicks = t.ladderClimbSpeed > 0 ? Math.ceil((spacing * HZ) / t.ladderClimbSpeed) : 0;
  const climbTicks = Math.max(t.ladderClimbTicks, speedTicks);
  s.ladderLiftTick++;
  const f = Math.min(1, s.ladderLiftTick / climbTicks);
  const lift = spacing * f * f * (3 - 2 * f);
  s.ladderLiftVelocity = (lift - before) / DT;
  s.ladderLiftY = lift;
  if (s.ladderLiftTick < climbTicks) return;
  s.barId++;
  s.ladderRung++;
  s.ladderLiftY = 0;
  s.ladderLiftTick = 0;
  s.popping = 0;
  s.ladderChain++;
  ev.push(Ev.Popped, s.tick, ladder, s.ladderRung, Grade.Perfect);
  linkOnPop(s, t, ev, s.barId);
}
